'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toasterApi } from '@terra/ui-design-system';
import { FindingStatusSchema } from '@terra/shared-schemas-orchestrator';
import { UnpublishedConstituentsErrorSchema } from '@terra/shared-contracts-findings';
import { terraProxyApiClient } from '../../../clients/testerClient';
import type { FindingsSelectionModel } from './useFindingsSelection';
import { useFindingsBulkConflictToast } from './useFindingsBulkConflictToast';

const MAX_CONSTITUENT_NAMES = 5;

const resolveConstituentNames = (body: unknown): string | null => {
  const parsed = UnpublishedConstituentsErrorSchema.safeParse(body);

  if (!parsed.success) {
    return null;
  }

  const names = parsed.data.violations.flatMap((violation) =>
    violation.unpublishedConstituents.map(
      (constituent) => constituent.title ?? constituent.id,
    ),
  );
  const visibleNames = names.slice(0, MAX_CONSTITUENT_NAMES).join(', ');

  return names.length > MAX_CONSTITUENT_NAMES
    ? `${visibleNames}…`
    : visibleNames;
};

export function useFindingsBulkPublish({
  selectionModel,
  onMutated,
}: {
  selectionModel: Pick<
    FindingsSelectionModel,
    'selection' | 'selectedCount' | 'clearSelection' | 'buildBulkFilterPayload'
  >;
  onMutated: () => Promise<void>;
}) {
  const tPublish = useTranslations('findingsTab.publish');
  const tFindingsRow = useTranslations('findingsTab.row');
  const toastBulkConflict = useFindingsBulkConflictToast();
  const [isPublishing, setIsPublishing] = useState(false);
  const { selection, selectedCount, clearSelection, buildBulkFilterPayload } =
    selectionModel;

  const publishSelection = useCallback(async () => {
    if (selectedCount === 0) {
      toasterApi.error({
        title: tPublish('noSelectionTitle'),
        description: tPublish('noSelectionDescription'),
      });

      return;
    }

    setIsPublishing(true);

    try {
      const update = {
        isPublished: true,
        status: FindingStatusSchema.enum.Unresolved,
      };
      const response =
        selection.mode === 'ids'
          ? await terraProxyApiClient.updateFindingsBulk(
              Array.from(selection.ids),
              update,
            )
          : await terraProxyApiClient.updateFindingsBulkByFilter(
              buildBulkFilterPayload(),
              update,
            );

      if (response.status === 200) {
        toasterApi.success({
          title: tPublish('successTitle'),
          description: tPublish('successDescription', {
            count: response.body.updatedIds.length,
          }),
        });
        clearSelection();
        await onMutated();

        return;
      }

      if (response.status !== 409) {
        throw new Error(tPublish('errorDescription'));
      }

      const constituentNames = resolveConstituentNames(response.body);

      if (constituentNames !== null) {
        toasterApi.error({
          title: tFindingsRow('publishConstituentsErrorTitle'),
          description: tFindingsRow('publishConstituentsErrorDescription', {
            names: constituentNames,
          }),
        });

        return;
      }

      toastBulkConflict({ body: response.body, tooManyTitleKey: 'publish' });
    } catch (error) {
      console.error('Failed to publish findings:', error);
      toasterApi.error({
        title: tPublish('errorTitle'),
        description:
          error instanceof Error ? error.message : tPublish('errorDescription'),
      });
    } finally {
      setIsPublishing(false);
    }
  }, [
    buildBulkFilterPayload,
    clearSelection,
    onMutated,
    selectedCount,
    selection,
    tFindingsRow,
    tPublish,
    toastBulkConflict,
  ]);

  return { isPublishing, publishSelection };
}
